import type { ReportCategory, ReportStatus } from '../../../sdk/@types/Forum';

/**
 * Rótulos curtos dos motivos, como aparecem nos chips da fila e do detalhe
 * (frame `Pgime`). O formulário do aluno usa a versão longa ("Lei ou norma
 * desatualizada", "Erro no enunciado ou nas alternativas") — aqui o espaço é
 * de um chip.
 */
export const REPORT_CATEGORY_LABELS: Record<ReportCategory, string> = {
  wrong_answer: 'Gabarito incorreto',
  outdated_law: 'Lei desatualizada',
  statement_error: 'Erro no enunciado',
  annulled: 'Anulada pela banca',
  other: 'Outro motivo',
};

export const REPORT_CATEGORY_OPTIONS: { value: ReportCategory; label: string }[] =
  (Object.keys(REPORT_CATEGORY_LABELS) as ReportCategory[]).map((value) => ({
    value,
    label: REPORT_CATEGORY_LABELS[value],
  }));

/**
 * Rótulo de cada sub-fila da triagem — nomeada pelo **que aconteceu**, não pelo
 * nome do estado na API.
 *
 * `accepted` era "Procedentes" quando era o único desfecho procedente. Com
 * `resolved` (procedente, questão corrigida) no jogo, os dois são procedentes e
 * o rótulo antigo passou a mentir: o que distingue `accepted` é a questão ter
 * sido **desativada**. O valor na API não mudou — só a etiqueta (decisão 5 do
 * design de `forum-notifications-and-triage-closure`).
 */
export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  open: 'Abertas',
  resolved: 'Corrigidas',
  accepted: 'Desativadas',
  rejected: 'Rejeitadas',
};

/** "3 denúncias" / "1 denúncia". */
export const reportCountLabel = (count: number) =>
  `${count} ${count === 1 ? 'denúncia' : 'denúncias'}`;

/** "3 alunos" / "1 aluno". */
export const studentCountLabel = (count: number) =>
  `${count} ${count === 1 ? 'aluno' : 'alunos'}`;

/**
 * Tempo relativo no formato do design: "há 20 min", "há 3 h", "ontem",
 * "há 2 dias". `formatDistanceToNow` do date-fns diria "há 20 minutos" e
 * "cerca de 3 horas" — bom para prosa, largo demais para a linha do card.
 */
export function relativeTime(value?: string, now: Date = new Date()): string {
  const ts = Date.parse(String(value ?? ''));
  if (Number.isNaN(ts)) return '—';

  const seconds = Math.max(0, Math.round((now.getTime() - ts) / 1000));
  if (seconds < 60) return 'agora';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'ontem';
  if (days < 30) return `há ${days} dias`;

  const months = Math.floor(days / 30);
  if (months < 12) return `há ${months} ${months === 1 ? 'mês' : 'meses'}`;

  const years = Math.floor(days / 365);
  return `há ${years} ${years === 1 ? 'ano' : 'anos'}`;
}

/** Iniciais do avatar do denunciante — "Glauber Cruz" → "GC". */
export function initials(name?: string): string {
  const parts = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** "CBMGO 2024 · Noções de direito", pulando o que o back não mandou. */
export function questionRefLine(ref?: {
  agency?: string;
  year?: string;
  subject?: string;
  topic?: string;
}): string {
  const agencyYear = [ref?.agency, ref?.year].filter(Boolean).join(' ');
  return [agencyYear, ref?.subject, ref?.topic].filter(Boolean).join(' · ');
}
